import { isAxiosError } from 'axios';
import $api from '@/http';

/** Одна позиция batch-запроса счётчиков. */
export interface IFolderCountItem {
    /** "tasks" | "documents" | "bk" — имя submenu (файл в Templates/Folders/). */
    submenu: string;
    /** Имя шаблона поиска (templateName/searchType), совпадает с файлом в Templates/SearchForm/. */
    name: string;
}

/** Ответ batch-подсчёта счётчиков. Ключ словаря — name из запроса. */
export interface IFolderCountsResponse {
    counts: Record<string, number>;
    /**
     * Сервер отдал часть значений из устаревшего кэша или пропустил часть items (минимальный интервал
     * пересчёта, перегрузка): повторить запрос этих же items не раньше чем через N мс. Ключи,
     * которых нет в `counts`, клиент не удаляет.
     */
    retryAfterMs?: number;
}

type FolderCountersKind = 'tasks' | 'documents';

/**
 * Окно склейки запросов. Folders и SectionHub получают одно событие CountersInvalidated (или возврат во
 * вкладку) и шлют запросы в пределах миллисекунд — второй присоединяется к запросу первого. Запрос, ушедший
 * до последнего события, вернул бы значения до изменения — к нему не присоединяемся.
 */
const JOIN_WINDOW_MS = 50;

/** 429 — сервер ограничил параллельные запросы пользователя (несколько видимых вкладок): повторить позже. */
const RATE_LIMITED_RETRY_MS = 1_000;

interface InflightRequest {
    startedAt: number;
    promise: Promise<IFolderCountsResponse>;
}

/** `${kind}|${submenu}/${name}` → запрос, в котором item уже считается. */
const inflight = new Map<string, InflightRequest>();
let lastInvalidationAt = 0;

const itemKey = (kind: FolderCountersKind, item: IFolderCountItem): string => `${kind}|${item.submenu}/${item.name}`;

const post = async (kind: FolderCountersKind, items: IFolderCountItem[]): Promise<IFolderCountsResponse> => {
    try {
        const res = await $api.post<IFolderCountsResponse>(
            `/folders/${kind}/counts`,
            { items, supportsRetryAfter: true },
            { suppressErrorNotification: true },
        );
        return res.data ?? { counts: {} };
    } catch (err) {
        if (isAxiosError(err) && err.response?.status === 429) {
            return { counts: {}, retryAfterMs: RATE_LIMITED_RETRY_MS + Math.random() * RATE_LIMITED_RETRY_MS };
        }
        throw err;
    }
};

/** Общий запрос без сигнала: его ждут несколько вызывающих, отмена одного не должна рвать остальных. */
const startShared = (kind: FolderCountersKind, items: IFolderCountItem[]): Promise<IFolderCountsResponse> => {
    const entry: InflightRequest = { startedAt: Date.now(), promise: post(kind, items) };
    const keys = items.map((item) => itemKey(kind, item));
    keys.forEach((key) => inflight.set(key, entry));
    const release = () => {
        keys.forEach((key) => {
            if (inflight.get(key) === entry) inflight.delete(key);
        });
    };
    entry.promise.then(release, release);
    return entry.promise;
};

const abortReason = (signal: AbortSignal): Error =>
    signal.reason instanceof Error ? signal.reason : new DOMException('Aborted', 'AbortError');

const withAbort = <T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> => {
    if (!signal) return promise;
    let onAbort = () => {};
    const aborted = new Promise<never>((_, reject) => {
        onAbort = () => reject(abortReason(signal));
        if (signal.aborted) onAbort();
        else signal.addEventListener('abort', onAbort, { once: true });
    });
    return Promise.race([promise, aborted]).finally(() => signal.removeEventListener('abort', onAbort));
};

const mergeResponses = (responses: IFolderCountsResponse[]): IFolderCountsResponse => {
    const merged: IFolderCountsResponse = { counts: {} };
    for (const res of responses) {
        Object.assign(merged.counts, res.counts);
        if (res.retryAfterMs != null) merged.retryAfterMs = Math.max(merged.retryAfterMs ?? 0, res.retryAfterMs);
    }
    return merged;
};

/**
 * Запрос с дедупом по item: items, которые уже считаются в запросе, ушедшем не раньше {@link JOIN_WINDOW_MS}
 * и после последнего события, берутся из него, остальные уходят одним новым запросом.
 */
const fetchCounts = (
    kind: FolderCountersKind,
    items: IFolderCountItem[],
    signal?: AbortSignal,
): Promise<IFolderCountsResponse> => {
    if (items.length === 0) return Promise.resolve({ counts: {} });
    const now = Date.now();
    const requests = new Set<Promise<IFolderCountsResponse>>();
    const rest: IFolderCountItem[] = [];
    for (const item of items) {
        const request = inflight.get(itemKey(kind, item));
        if (request && now - request.startedAt <= JOIN_WINDOW_MS && request.startedAt >= lastInvalidationAt) {
            requests.add(request.promise);
        } else {
            rest.push(item);
        }
    }
    if (rest.length > 0) requests.add(startShared(kind, rest));
    return withAbort(Promise.all(requests).then(mergeResponses), signal);
};

/**
 * Клиент для /api/v1/folders/{tasks|documents}/counts. Сервер добавляет присланные items в подписки
 * пользователя на push. suppressErrorNotification — фоновые запросы не выстреливают generic-тостом;
 * 404 (FolderCounters.Enabled=false) вызывающий молча проглатывает, 429 превращается в retryAfterMs.
 */
export class FolderCountersService {
    static fetchTasks(items: IFolderCountItem[], signal?: AbortSignal): Promise<IFolderCountsResponse> {
        return fetchCounts('tasks', items, signal);
    }

    static fetchDocuments(items: IFolderCountItem[], signal?: AbortSignal): Promise<IFolderCountsResponse> {
        return fetchCounts('documents', items, signal);
    }

    /** Пришло CountersInvalidated: запросы, ушедшие раньше, несут значения до изменения — не склеиваться с ними. */
    static noteInvalidated(): void {
        lastInvalidationAt = Date.now();
    }
}
